import React from 'react'
import { Sparkles } from 'lucide-react'
import { EnrichmentProgress } from '../../types'

interface EnrichmentProgressProps {
  progressData: EnrichmentProgress | null;
  pending: number;
}

const EnrichmentProgressBar: React.FC<EnrichmentProgressProps> = ({progressData, pending}) => {
  if (!progressData && pending === 0) {
    return null;
  }

  const progress = progressData?.progress || 0;
  const total = progressData?.total || pending;
  const done = total - pending;
  const status = progressData?.status || 'Enriching data';

  return (
    <div className="inline-flex items-center gap-3 bg-gray-900 rounded-full pl-3 pr-4 py-1.5 shadow-sm">
      {/* Sparkle icon with subtle animation */}
      <div className="relative flex-shrink-0">
        <Sparkles
          className="w-3.5 h-3.5 text-purple-400"
          style={{ animation: 'enrichment-sparkle 2s ease-in-out infinite' }}
        />
      </div>

      {/* Status text */}
      <span className="text-[12px] font-medium text-white whitespace-nowrap">
        {status}
      </span>

      {/* Animated dots */}
      <span className="flex gap-0.5">
        <span className="w-1 h-1 bg-purple-400 rounded-full" style={{ animation: 'enrichment-dot 1.4s ease-in-out infinite' }} />
        <span className="w-1 h-1 bg-purple-400 rounded-full" style={{ animation: 'enrichment-dot 1.4s ease-in-out 0.2s infinite' }} />
        <span className="w-1 h-1 bg-purple-400 rounded-full" style={{ animation: 'enrichment-dot 1.4s ease-in-out 0.4s infinite' }} />
      </span>

      {/* Progress bar */}
      <div className="w-20 h-1.5 bg-gray-700 rounded-full relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #8B5CF6 0%, #A855F7 50%, #C084FC 100%)'
          }}
        />
        {/* Shimmer effect */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
            backgroundSize: '200% 100%',
            animation: 'enrichment-shimmer 1.5s ease-in-out infinite'
          }}
        />
      </div>

      {/* Progress percentage */}
      <span className="text-[11px] font-semibold text-purple-400 min-w-[32px] text-right">
        {Math.round(progress)}%
      </span>

      {/* Divider */}
      <div className="w-px h-3 bg-gray-700" />

      {/* Count */}
      <span className="text-[11px] text-gray-400 whitespace-nowrap">
        <span className="font-medium text-gray-300">{done}</span>/{total}
      </span>

      {/* Animations */}
      <style>{`
        @keyframes enrichment-sparkle {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
        @keyframes enrichment-dot {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
        @keyframes enrichment-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  )
}

export default EnrichmentProgressBar;
